import {
    Alert, Autocomplete, Box, Button, MenuItem, Paper, Stack, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import React from 'react';

import { RemoteAutocomplete } from '../../components/RemoteAutocomplete';
import DeleteIconButton from '../../components/DeleteIconButton';
import { CONDITION_MATCH_MODES, MATCH_TYPES, MAX_CONDITIONS } from './constants';
import SenderField, { EMPTY_SENDER } from './SenderField';
import TagConditionField, { EMPTY_TAG_CONDITION } from './TagConditionField';

// The whole condition list of a blacklist rule (CP-8268): several conditions
// combined with "Match all (AND)" or "Match any (OR)".
//
// Every condition renders through the same row, including the first — the
// backend stores condition #1 on the rule row and the rest as child rows, but
// that split is invisible here and in the payload (the API takes one ordered
// `conditions` array).
//
// Value shape (owned by the caller):
//   { mode: 'all'|'any', conditions: [{ _uid, match_type, pattern, user,
//     sender_user, sender_access_group, ota, tag_condition }] }
// `user` / `sender_user` / `sender_access_group` hold {id,...} objects (or null)
// so existing rules label their pickers without extra lookups; `_uid` is a
// client-only React key — rows can be removed, and index keys would hand a
// removed row's in-progress picker state to whichever condition shifts up.

let uidCounter = 0;
const newUid = () => {
    uidCounter += 1;
    return `c${uidCounter}-${Date.now()}`;
};

export const EMPTY_CONDITION = {
    match_type: 'exact',
    pattern: '',
    user: null,
    ota: '',
    tag_condition: EMPTY_TAG_CONDITION,
    ...EMPTY_SENDER,
};

export const newCondition = (base = EMPTY_CONDITION) => ({ ...base, _uid: newUid() });

export const EMPTY_CONDITIONS = { mode: 'all', conditions: [] };

export const emptyConditions = () => ({ mode: 'all', conditions: [newCondition()] });

// The three destination-string types share the pattern field; switching between
// them keeps the typed text (the old single-condition dialog preserved it too).
const DESTINATION_TYPES = ['exact', 'substring', 'regex'];

/** One API condition -> this field's condition shape. */
export const conditionFromApi = (c) => newCondition({
    ...EMPTY_CONDITION,
    match_type: c.match_type,
    pattern: c.pattern || '',
    user: c.user || null,
    ota: c.ota ? c.ota.id : '',
    sender_user: c.sender_user || null,
    sender_access_group: c.sender_access_group || null,
    tag_condition: c.match_type === 'tag' ? {
        chips: (c.tags || []).map((chip) => (chip.tag
            ? { tag_id: chip.tag.id }
            : { category_code: chip.category_code, tag_name: chip.tag_name })),
        match_mode: c.tag_match_mode || 'and',
        sources: (c.tag_sources || []).length > 0 ? c.tag_sources : ['booking'],
    } : EMPTY_TAG_CONDITION,
});

/** This field's condition shape -> the API's, carrying only the used keys. */
export const conditionToApi = (cond) => {
    const out = { match_type: cond.match_type };
    switch (cond.match_type) {
    case 'user':
        out.user = cond.user ? Number(cond.user.id) : null;
        break;
    case 'ota':
        out.ota = Number(cond.ota);
        break;
    case 'sender':
        if (cond.sender_user) out.sender_user = Number(cond.sender_user.id);
        else if (cond.sender_access_group) out.sender_access_group = Number(cond.sender_access_group.id);
        break;
    case 'tag': {
        const tc = cond.tag_condition || EMPTY_TAG_CONDITION;
        out.tags = tc.chips.map((chip) => (chip.tag_id != null
            ? { tag: chip.tag_id }
            : { category_code: chip.category_code, tag_name: chip.tag_name }));
        out.tag_match_mode = tc.match_mode;
        out.tag_sources = tc.sources;
        break;
    }
    default:
        out.pattern = (cond.pattern || '').trim();
    }
    return out;
};

/** The first problem with `cond`, or null. Shared with the dialog's save guard. */
export const conditionError = (cond) => {
    switch (cond.match_type) {
    case 'user':
        return cond.user ? null : 'Pick a user for the User condition.';
    case 'ota':
        return cond.ota ? null : 'Pick an OTA for the OTA condition.';
    case 'reseller':
        return (cond.pattern || '').trim() ? null : 'A reseller name is required.';
    case 'sender':
        return (cond.sender_user || cond.sender_access_group)
            ? null : 'Pick a sender user or access group.';
    case 'tag': {
        const tc = cond.tag_condition || EMPTY_TAG_CONDITION;
        if (tc.chips.length === 0) return 'Add at least one tag to the tag condition.';
        if (tc.sources.length === 0) return 'Pick at least one tag source.';
        return null;
    }
    default:
        return (cond.pattern || '').trim() ? null : 'A match pattern is required.';
    }
};

/** "reseller: Viator" / "3 tags (AND)" — the one-line summary of a condition. */
export const conditionSummary = (cond) => {
    const label = (MATCH_TYPES.find((t) => t.value === cond.match_type) || {}).label
        || cond.match_type;
    switch (cond.match_type) {
    case 'user':
        return `${label}: ${cond.user ? (cond.user.email || cond.user.id) : '?'}`;
    case 'ota':
        return `${label}: #${cond.ota || '?'}`;
    case 'sender':
        if (cond.sender_user) return `${label}: ${cond.sender_user.email || cond.sender_user.id}`;
        return `${label}: ${cond.sender_access_group ? cond.sender_access_group.name : '?'}`;
    case 'tag': {
        const tc = cond.tag_condition || EMPTY_TAG_CONDITION;
        return `${label}: ${tc.chips.length} tag(s) ${(tc.match_mode || 'and').toUpperCase()}`;
    }
    default:
        return `${label}: ${cond.pattern || ''}`;
    }
};

const userLabel = (u) => {
    if (!u) return '';
    return u.name ? `${u.name} (${u.email})` : (u.email || `#${u.id}`);
};

const ConditionRow = ({ cond, index, total, otas, resellers, tagRegistry, onChange, onRemove }) => {
    const set = (patch) => onChange({ ...cond, ...patch });
    const isTag = cond.match_type === 'tag';
    const isSender = cond.match_type === 'sender';
    const error = conditionError(cond);

    return (
        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 78 }}>
                    Condition {index + 1}
                </Typography>
                <TextField select size="small" label="Match type" value={cond.match_type}
                    sx={{ flex: 1 }}
                    onChange={(e) => {
                        const next = e.target.value;
                        // Reset the previous type's target so a switch cannot leave a
                        // stale value behind (the backend ignores unused keys too) —
                        // but keep the typed pattern when hopping between the three
                        // destination-string types.
                        const keepPattern = DESTINATION_TYPES.includes(next)
                            && DESTINATION_TYPES.includes(cond.match_type);
                        set({
                            ...EMPTY_CONDITION,
                            match_type: next,
                            ...(keepPattern ? { pattern: cond.pattern } : {}),
                        });
                    }}>
                    {MATCH_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
                {total > 1 && (
                    <DeleteIconButton title="Remove this condition" onClick={onRemove} />
                )}
            </Stack>

            {cond.match_type === 'user' && (
                <RemoteAutocomplete
                    size="small"
                    fullWidth
                    resource="blacklist_rule"
                    urlSuffix="sender_users/"
                    label="User"
                    value={cond.user}
                    getOptionLabel={userLabel}
                    filterOptions={(options) => options}
                    onChange={(_, v) => set({ user: v || null })}
                    textFieldProps={{ helperText: 'The user whose deliveries to block' }} />
            )}
            {cond.match_type === 'ota' && (
                <Autocomplete size="small" options={otas}
                    value={otas.find((o) => o.id === cond.ota) || null}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, val) => set({ ota: val ? val.id : '' })}
                    renderInput={(params) => <TextField {...params} label="Online travel agency" required
                        helperText="The destination is matched to this OTA" />} />
            )}
            {cond.match_type === 'reseller' && (
                <Autocomplete size="small" freeSolo options={resellers} value={cond.pattern}
                    onInputChange={(_, val) => set({ pattern: val || '' })}
                    renderInput={(params) => <TextField {...params} label="Reseller" required
                        helperText="The booking-source (Reseller tag) of the booking; blocks every delivery of a matching booking, regardless of destination" />} />
            )}
            {isSender && (
                <SenderField
                    value={{ sender_user: cond.sender_user, sender_access_group: cond.sender_access_group }}
                    onChange={(v) => set(v)} />
            )}
            {isTag && (
                <TagConditionField
                    value={cond.tag_condition || EMPTY_TAG_CONDITION}
                    onChange={(v) => set({ tag_condition: v })}
                    registry={tagRegistry}
                    error={(cond.tag_condition || EMPTY_TAG_CONDITION).chips.length === 0} />
            )}
            {!['user', 'ota', 'reseller', 'tag', 'sender'].includes(cond.match_type) && (
                <TextField label="Match pattern" value={cond.pattern} size="small"
                    onChange={(e) => set({ pattern: e.target.value })} fullWidth required
                    helperText={cond.match_type === 'regex'
                        ? 'A regular expression matched against the destination'
                        : 'Matched against the destination'} />
            )}
            {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Paper>
    );
};

const ConditionsField = ({ value, onChange, otas = [], resellers = [], tagRegistry }) => {
    const { mode = 'all', conditions = [] } = value || {};
    const set = (patch) => onChange({ mode, conditions, ...patch });

    const replaceAt = (i, cond) => set({
        conditions: conditions.map((c, idx) => (idx === i ? cond : c)),
    });
    const removeAt = (i) => set({ conditions: conditions.filter((_, idx) => idx !== i) });
    const add = () => set({ conditions: [...conditions, newCondition()] });

    const hasSender = conditions.some((c) => c.match_type === 'sender');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {conditions.length > 1 && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        Block a delivery when
                    </Typography>
                    <ToggleButtonGroup size="small" exclusive value={mode}
                        onChange={(e, v) => { if (v) set({ mode: v }); }}>
                        {CONDITION_MATCH_MODES.map((m) => (
                            <ToggleButton key={m.value} value={m.value} sx={{ py: 0.25, px: 1, fontSize: 12 }}>
                                {m.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>
            )}

            {conditions.map((cond, i) => (
                <ConditionRow key={cond._uid || i} cond={cond} index={i} total={conditions.length}
                    otas={otas} resellers={resellers} tagRegistry={tagRegistry}
                    onChange={(c) => replaceAt(i, c)} onRemove={() => removeAt(i)} />
            ))}

            <Box>
                <Button size="small" onClick={add} disabled={conditions.length >= MAX_CONDITIONS}>
                    Add condition
                </Button>
                {conditions.length >= MAX_CONDITIONS && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        At most {MAX_CONDITIONS} conditions.
                    </Typography>
                )}
            </Box>

            {hasSender && (
                <Alert severity="info" sx={{ py: 0 }}>
                    A Sender condition only ever matches the chat-message notifications —
                    &quot;message received&quot;, &quot;recent messages&quot; and &quot;unread
                    messages&quot;. Every other event carries no chat message, so it has no
                    sender to compare.
                </Alert>
            )}
        </Box>
    );
};

export default ConditionsField;
